<template>
    <div>
        <DocumentPreview
            ref="preview"
            :product="product"
            :file="previewFile"
        />
        <div class="mb-1">
            <ChangeLogModal
                name="compositionChangeLog"
                ref="changelog"
                :relations-label="$t('Component(Composition):changelog-substances-label')"
                :primary-key="id"
                business-object="Product"
                namespace="subscriber"
            />
            <div class="text-right">
                <Button class="btn-sm" @click="$refs.changelog.show()">{{ $t('Component(Button):changelog') }}</Button>
            </div>
        </div>
        <ComplianceOverview
            v-if="showComplianceOverview"
            :product-id="product.id"
            :event-statuses="product.eventStatuses"
            :legal-roles="product.legalRoles"
            :status-explanation-revision="statusExplanationRevision"
        />
        <b-card :title="$t('Page(ProductCard):Component(Text):product-information')" border-variant="light">
            <b-row>
                <b-col md="6">
                    <product-attribute
                        id="product-name"
                        :label="$t('Page(NewProduct):Component(Text):product-name')"
                        :placeholder="$t('Page(NewProduct):Component(Placeholder):product-name')"
                        :value-placeholder="$t('Page(NewProduct):Component(Placeholder):product-name-value')"
                        :options="attributes['ProductName'] || []"
                        :selected="product.names"
                        :hidden-elements-title="$t('Page(ProductCard):Component(ProductNames):hidden-names-collapse')"
                        :is-attribute-visible="isNameVisible"
                        @change="onNameChange"
                        @add="addName"
                        @remove="removeName"
                    />
                </b-col>
                <b-col md="6">
                    <product-attribute
                        id="product-identifier"
                        ref="productIdentifiers"
                        :show-radio="showEchaPrimaryKeySelector"
                        :radio-value="productIdentifierEchaRadioValue"
                        :label="$t('Page(NewProduct):Component(Text):product-id')"
                        :placeholder="$t('Page(NewProduct):Component(Placeholder):product-id')"
                        :value-placeholder="$t('Page(NewProduct):Component(Placeholder):product-id-value')"
                        :options="attributes['ProductId'] || []"
                        :selected="product.identifiers"
                        :show-radio-for-item="showRadioForIdentifier"
                        :can-remove="isIdentifierRemovable"
                        :is-attribute-visible="isIdentifierVisible"
                        :hidden-elements-title="$t('Page(ProductCard):Component(ProductIdentifiers):hidden-identifiers-collapse')"
                        @change="onIdChange"
                        @add="addId"
                        @remove="removeId"
                        @radio="onPrimaryKeyChange"
                    />
                </b-col>
            </b-row>
            <b-row v-if="showProductWeight">
                <b-col>
                    <ProductWeight
                        :value="weight"
                        :required="productWeightRequired"
                        @input="onWeightChange"
                    />
                </b-col>
            </b-row>
            <b-row v-if="showBusinessRoles">
                <b-col md="12">
                    <BusinessRoles
                        :placeholder="$t('Page(NewProduct):Component(Placeholder):business-roles')"
                        :selected="product.businessRoles"
                        @change="setBusinessRoles"
                    />
                </b-col>
            </b-row>
            <b-row v-if="showIncludedIntoProductGroups">
                <b-col md="12">
                    <IncludedProductGroups
                        :placeholder="$t('Page(ProductCard):Component(Placeholder):included-product-groups')"
                        :selected="product.productGroups"
                        @loaded="onProductGroupsLoad"
                        @change="onParentGroupsChange"
                    />
                </b-col>
            </b-row>
            <b-row v-if="showCountryOfOrigin">
                <b-col>
                    <CountrySelector
                        :label="$t('Component(Label):manufacturing-origin')"
                        :selected="product.countryOfOrigin"
                        @input="onCountryOfOriginChange"
                    />
                </b-col>
            </b-row>
            <b-row v-if="showProductDocument">
                <b-col>
                    <ProductDocument
                        :file="productDescriptionDocument"
                        :label="$t('Component(Label):product-description-document')"
                        label-for="product-document"
                        @input="productDescriptionChange"
                        @download="download"
                        @preview="previewDocument"
                    />
                </b-col>
            </b-row>
            <b-row>
                <b-col>
                    <b-form-group
                        :label="$t('Component(Label):comment')"
                        label-for="product-comment"
                    >
                        <b-form-textarea
                            id="product-comment"
                            :value="product.comment || ''"
                            rows="2"
                            max-rows="8"
                            :placeholder="$t('Component(Label):comment')"
                            @input="onCommentChange"
                        />
                    </b-form-group>
                </b-col>
            </b-row>
        </b-card>
        <SupplierSelector
            v-if="isPurchasedProduct"
            :selected="suppliers"
            :add="canAddSupplier"
            @change="onSupplierChange"
        />
        <RegulatedProductStatementDeclaration
            v-if="showRegulatedProductStatements"
            border-variant="light"
            selection-mode="radio"
            show-comment
            :properties="regulatedProductStatementRows"
            @change="onRegulatedProductStatementsChange"
        />
        <ProductCategorySelect
            v-if="showProductCategorySelector"
            border
            :selected-items="product.categories"
            @change="onProductCategoryChange"
        />
        <ProductMaterialMixtureCategorySelector
            v-if="showMaterialMixtureCategorySelector"
            :material-category-ids="product.materialCategoryIds"
            :mixture-category-ids="product.mixtureCategoryIds"
            @change="onMaterialMixtureCategoryChange"
        />
        <Rohs
            v-if="showRohs"
            :product="product"
            @change="onRohsChange"
        />
        <IncludedProduct
            :type="product.type"
            :selected="product.includedProducts"
            :show-add-button="showAddButton"
            :has-concern-elements="hasConcernElements"
            :show-product-search="showProductOrComponentSearch"
            border
            @addProduct="addProductAndInclude"
            @clicked="onIncludedProductClick"
            @change="onIncludedProductChange"
        />
    </div>
</template>

<script>
import { BCardTitle, BCard, BRow, BCol, BFormGroup, BFormInput, BFormTextarea } from 'bootstrap-vue'
import {
    ProductCategorySelect, ProductAttribute, IncludedProduct, IncludedProductGroups, ComplianceOverview, DocumentPreview
} from "@core/components/product";
import {BusinessRoles, SupplierSelector} from "@core/components/supply-chain";
import {ProductApi} from "@core/api";
import {ProductIdentifiers, ProductNames, ProductType} from "@/libs/product";
import {clearCachedProduct} from "@core/domain/product";
import {CountrySelector} from "@core/components/supply-chain";
import {Rohs} from "@core/components/product/legislation";
import {i18n} from "@/libs/i18n";
import {LegislationKey} from "@core/domain/legislation";
import ProductDocument from "@/views/product/components/product-card/ProductDocument.vue";
import DownloadProductDocument from "@core/mixins/download-product-document";
import ChangeLogModal from "@core/components/common/changelog/ChangeLogModal.vue";
import Button from "@core/components/common/Button.vue";
import productIdentifier from "@/views/product/mixin/productIdentifier";
import {isChemity, isSustainity} from "@/libs/application";
import RegulatedProductStatementDeclaration from "@core/components/product/RegulatedProductStatementDeclaration.vue";
import {ProductWeight} from "@/views/product/components/product-card/common/characteristics";
import ProductMaterialMixtureCategorySelector from "@/views/product/components/product-card/common/categories";
import MaterialMixtureCategoryLegislationMixin from "@/views/product/components/product-card/common/categories/material-mixture-category-legislation-mixin";

export default {
    mixins: [DownloadProductDocument, productIdentifier, MaterialMixtureCategoryLegislationMixin],
    components: {
        RegulatedProductStatementDeclaration,
        Button, ChangeLogModal, BFormInput, BFormTextarea, DocumentPreview, ProductDocument,
        ComplianceOverview, CountrySelector, SupplierSelector, BRow, BCol, BFormGroup, BCard, BCardTitle,
        ProductAttribute, BusinessRoles, IncludedProductGroups, IncludedProduct, ProductCategorySelect, Rohs,
        ProductWeight, ProductMaterialMixtureCategorySelector
    },
    props: {
        product: {
            type: Object,
            default() {
                return {
                    type: null,
                    names: [],
                    identifiers: [],
                    businessRoles: [],
                    productGroups: [],
                    includedProducts: [],
                    eventStatuses: {},
                    legalRoles: [],
                    materialCategoryIds: [],
                    mixtureCategoryIds: [],
                    regulatedProductStatements: []
                };
            }
        },
        isRohsEnabled: {
            default: true
        },
        suppliers: {
            type: Array,
            default() {
                return [];
            }
        },

        addNewComponentButton: {
            default: false,
        },

        productDescriptionDocument: {
            default() {
                return {};
            }
        },

        id: {
            required: true
        },

        hasConcernElements: {
            default: false,
            type: Boolean
        },

        embedded: {
            default: false
        },
        productWeightRequired: {
            default: false
        },
        statusExplanationRevision: {
            type: Number,
            default: 1
        }
    },
    data() {
        return {
            canAddSupplier: isChemity(),
            previewFile: null,
            attributes: {},
            productIdentifierEchaRadioValue: 0,
            productGroups: [],
            isGroupsLoaded: false,
            weight: null,

            generalProductStatements: [],
            generalProductStatementsLoaded: false,
            regulatedProductStatementRows: [],
            isChemity: isChemity()
        };
    },
    methods: {
        productDescriptionChange(file) {
            this.$emit('productDescriptionChange', file);
        },
        onCommentChange(comment) {
            this.$emit('commentChange', comment);
        },
        download(file, done) {
            this
                .downloadProductDocument(this.product.id, file)
                .then(done);
        },
        previewDocument(file) {
            this.previewFile = file;
            this.$nextTick(() => {
                this.$refs.preview.show();
            });
        },
        isNameVisible(nameContainer) {
            if (isSustainity()) {
                return nameContainer.item.key !== ProductNames.SCIP_NAME;
            }
            return true;
        },
        isIdentifierRemovable(index, identifier) {
            if (!this.showRadioForIdentifier(identifier)) {
                return true;
            }
            if (this.product.identifiers.length === 1 && this.showEchaPrimaryKeySelector) {
                return false;
            }

            if (this.showEchaPrimaryKeySelector) {
                let i = 0;
                let radioAwareIdentifiersCount = 0;

                for (; i < this.product.identifiers.length; ++i) {
                    if (this.showRadioForIdentifier(this.product.identifiers[i])) {
                        radioAwareIdentifiersCount++;
                    }
                }

                if (radioAwareIdentifiersCount === 1 && this.product.identifiers.length === 2) {
                    if (!this.showRadioForIdentifier(this.product.identifiers[0]) || !this.showRadioForIdentifier(this.product.identifiers[1])) {
                        return false;
                    }
                }

                return radioAwareIdentifiersCount > 0;
            }

            return true;
        },
        onRohsChange(settings) {
            this.$emit('legislationDataChange', {
                key: 'rohs',
                settings: settings
            });
        },
        getIdentifiersWithKey(key) {
            return this.product.identifiers.filter(i => i.key === key);
        },
        onPrimaryKeyChange({index, attribute}) {
            let pkIndex = 0;
            const attributes = this.getIdentifiersWithKey(attribute.key);
            for (let i = 0; i < attributes.length; ++i) {
                if (attribute === attributes[i]) {
                    break;
                }
                pkIndex++;
            }
            this.productIdentifierEchaRadioValue = index;
            const currentSettings = {
                type: this.product.scipSettings.primaryKeyType,
                index: this.product.scipSettings.primaryKeyTypeIndex
            };
            const desiredSettings = {
                index: pkIndex,
                key: attribute.key
            };
            this.product.scipSettings.primaryKeyType = attribute.key;
            this.product.scipSettings.primaryKeyTypeIndex = pkIndex;

            if (this.shouldShowPrimaryKeyChangeWarning(attribute, currentSettings, desiredSettings)) {
                this
                    .showPrimaryKeyChangeWarning()
                    .then(result => {
                        if (!result.isConfirmed) {
                            this.restoreScipProductIdentifierRadioValue();
                        } else {
                            this.product.submissionDetails.primaryKeySettings.confirmedChange = true;
                        }
                    })
            }
        },
        shouldShowPrimaryKeyChangeWarning(attribute, currentSettings, desiredSettings) {
            if (this.product.hasBeenScipNotified && null !== this.product.submissionDetails) {
                const primaryKeySettings = this.product.submissionDetails.primaryKeySettings; // pk that was used for notification
                if (primaryKeySettings) {
                    //  warning should be shown only when changing from pk that was used in scip notification
                    if (
                        currentSettings.type !== primaryKeySettings.type
                        || currentSettings.index !== primaryKeySettings.index
                    ) {
                        return false;
                    }
                    if (primaryKeySettings.index === desiredSettings.index && desiredSettings.key === primaryKeySettings.type) {
                        //  pk has been change to the option that was used during scip notification
                        primaryKeySettings.confirmedChange = false;
                    } else {
                        //  check if user has confirmed pk change
                        if (primaryKeySettings.hasOwnProperty('confirmedChange') && primaryKeySettings.confirmedChange) {
                            return false;
                        }
                        return true;
                    }
                }
            }
            return false;
        },
        showPrimaryKeyChangeWarning() {
            return this.$swal({
                title: i18n.t('Component(Prompt):title-warning'),
                text: i18n.t('Component(ScipPrimaryKeyChangePrompt):text'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: i18n.t('Component(Button):continue'),
                cancelButtonText: i18n.t('Component(Button):cancel'),
                customClass: {
                    confirmButton: 'btn btn-danger',
                    cancelButton: 'btn btn-outline-secondary ml-1',
                },
                buttonsStyling: false,
            });
        },
        onWeightChange(weight) {
            this.weight = weight;
            this.emitAttributesChange();
        },
        emitAttributesChange() {
            this.$emit('characteristicsChange', {
                weight: parseFloat(this.weight)
            })
        },
        onIncludedProductClick(includedProduct) {
            this.$emit('includedProductClick', includedProduct);
        },
        onSupplierChange(idList, suppliers) {
            this.$emit('suppliersChange', idList, suppliers);
        },
        onNameChange(index, option) {
            // this.product.names[index].key = option.key;
            // this.product.names[index].value = option.value;
            // this.$emit('nameChange', this.product.names[index]);
        },
        onCountryOfOriginChange(value) {
            this.product.countryOfOrigin = value;
            this.$emit('countryOfOriginChange', value);
        },
        addName(object) {
            const name = {
                key: object.key,
                value: object.value,
            };
            this.product.names.push(name);
            this.$emit('newName', name);
        },
        removeName(index) {
            const name = this.product.names[index];
            this.product.names.splice(index, 1);
            this.$emit('removeName', name);
        },
        onIdChange(index, option) {
            //  HAM-512
            if (this.showEchaPrimaryKeySelector) {
                //  do we have all needed information
                if (this.product.hasBeenScipNotified && null !== this.product.submissionDetails) {
                    //  if user edits another type then skip
                    if (this.product.submissionDetails.primaryKeySettings.type !== option.key) {
                        return;
                    }

                    if (option.hasOwnProperty('confirmedChange') && option.confirmedChange) {
                        return;
                    }

                    let notifiedType = this.product.submissionDetails.primaryKeySettings.type;
                    let k = 0;
                    let i = 0;
                    let found = false;
                    //  we need to be sure that user edits pk that has been sent
                    for (i = 0; i <= index; ++i) {
                        if (this.product.identifiers[i].key === notifiedType) {
                            if (this.product.submissionDetails.primaryKeySettings.index === k) {
                                found = true;
                                break;
                            }
                            k++;
                        }
                    }

                    if (index === i && found && this.productIdentifierEchaRadioValue === i) {
                        //  user edits pk that has been used
                        if (option.value !== this.product.submissionDetails.primaryKeyValue) {
                            const primaryKeySettings =  this.product.submissionDetails.primaryKeySettings;
                            if (!primaryKeySettings) {
                                return;
                            }
                            if (primaryKeySettings.hasOwnProperty('confirmedChange') && primaryKeySettings.confirmedChange) {
                                return;
                            }

                            this.showPrimaryKeyChangeWarning()
                                .then(result => {
                                    if (!result.isConfirmed) {
                                        option.value = this.product.submissionDetails.primaryKeyValue;
                                        primaryKeySettings.confirmedChange = false;
                                    } else {
                                        primaryKeySettings.confirmedChange = true;
                                    }
                                });
                        }
                    }
                }
            }
        },
        addId(object) {
            const id = {
                key: object.key,
                value: object.value,
            };
            this.product.identifiers.push(id);
            this.$emit('newId', id);
        },
        removeId(index) {
            //  HAM-514
            if (this.showEchaPrimaryKeySelector) {
                if (this.productIdentifierEchaRadioValue === index) {
                    let i = 0;
                    for (; i < this.product.identifiers.length; ++i) {
                        if (i === index) {
                            continue;
                        }
                        if (this.showRadioForIdentifier(this.product.identifiers[i])) {
                            break;
                        }
                    }

                    this.productIdentifierEchaRadioValue = i > index ? --i : i;
                } else {
                    if (this.productIdentifierEchaRadioValue > index) {
                        this.productIdentifierEchaRadioValue--;
                    }
                }
                this.product.identifiers.splice(index, 1);

                this.onPrimaryKeyChange({
                    index: this.productIdentifierEchaRadioValue,
                    attribute: this.product.identifiers[this.productIdentifierEchaRadioValue]
                });
            } else {
                this.product.identifiers.splice(index, 1);
            }

            this.$emit('removeId');
        },
        setBusinessRoles(roles) {
            this.product.businessRoles = roles;
            this.$emit('businessRolesChange', roles);
        },
        onProductCategoryChange({idList, items}) {
            this.product.categories = items.map(item => item.category)
            this.$emit('productCategoriesChange', items);
        },
        onMaterialMixtureCategoryChange({materialCategoryIds, mixtureCategoryIds}) {
            this.$set(this.product, 'materialCategoryIds', materialCategoryIds);
            this.$set(this.product, 'mixtureCategoryIds', mixtureCategoryIds);
        },
        onIncludedProductChange() {
            this.$emit('includedProductChange');
        },
        onProductGroupsLoad(groups) {
            this.productGroups = groups;
            this.isGroupsLoaded = true;
        },
        onParentGroupsChange(groups) {
            this.product.productGroups = groups;
            this.$emit('parentGroupsChange');
        },
        addProductAndInclude() {
            const productId = this.product.id;
            this.$emit('addProductAndIncludeClick');
            const redirectHandler = ($router, componentId) => {
                clearCachedProduct(productId);
                $router.push({
                    name: 'product-card',
                    params: {
                        id: componentId,
                        keepBreadcrumbs: true,
                        topLevelProduct: productId,
                        embedded: this.embedded
                    }
                });
            };

            if (this.isPurchasedProductComponent || this.isPurchasedProduct) {
                this.$router.push({
                    name: 'product-new',
                    params: {
                        type: ProductType.COMPONENT_TO_COMPLEX_PURCHASED_PRODUCT,
                        redirect: redirectHandler,
                        includeTo: this.product.id,
                    },
                    props: {}
                });
            } else if (this.product.type === ProductType.FICTITIOUS) {
                this.$router.push({
                    name: 'product-new',
                    params: {
                        type: ProductType.PURCHASED,
                        redirect: redirectHandler,
                        includeTo: this.product.id,
                    },
                    props: {}
                });
            } else {
                this.$router.push({
                    name: 'product-new-wizard',
                    params: {
                        redirect: redirectHandler,
                        includeTo: this.product.id,
                        displayTypes: [
                            ProductType.OWN,
                            ProductType.PURCHASED
                        ]
                    }
                });
            }
        },
        restoreScipProductIdentifierRadioValue() {
            if (!this.product.submissionDetails || !this.product.submissionDetails.primaryKeySettings) {
                return;
            }
            const index = this.calculateProductIdentifierRadioValue(
                this.product.submissionDetails.primaryKeySettings.type,
                this.product.submissionDetails.primaryKeySettings.index
            );

            this.product.scipSettings.primaryKeyType = this.product.identifiers[index].key;
            this.product.scipSettings.primaryKeyTypeIndex = this.product.submissionDetails.primaryKeySettings.index;

            this.productIdentifierEchaRadioValue = index;
        },
        calculateProductIdentifierRadioValue(primaryKeyType, primaryKeyTypeIndex) {
            let index = 0;
            let pkIndex = 0;

            for (let i = 0; i < this.product.identifiers.length; ++i) {
                if (this.product.identifiers[i].key === primaryKeyType) {
                    if (pkIndex === primaryKeyTypeIndex) {
                        index = i;
                        break;
                    }
                    pkIndex++;
                }
            }

            return index;
        },
        onRegulatedProductStatementsChange(rows) {
            this.regulatedProductStatementRows = rows;
            this.syncRegulatedProductStatementsToProduct();
        },
        syncRegulatedProductStatementsToProduct() {
            this.product.regulatedProductStatements = this.regulatedProductStatementRows;
        },
        recalculateProductStatements() {
            if (!this.generalProductStatementsLoaded) {
                return;
            }
            if (!this.product || !this.product.id || !(this.product.regulatedProductStatements instanceof Array)) {
                return;
            }
            const regulatedPropertiesMap = {};
            this.product.regulatedProductStatements.forEach(rs => {
                const statementId = rs.statement && rs.statement.id ? rs.statement.id : rs.statement;
                if (!statementId) {
                    return;
                }
                regulatedPropertiesMap[statementId] = {
                    coveredExcluded: rs.coveredExcluded,
                    comment: Object.prototype.hasOwnProperty.call(rs, 'comment') ? rs.comment : null,
                };
            });
            this.regulatedProductStatementRows = this.generalProductStatements.map(statement => {
                const regulatedProperty = Object.prototype.hasOwnProperty.call(regulatedPropertiesMap, statement.id)
                    ? regulatedPropertiesMap[statement.id]
                    : {coveredExcluded: null, comment: null};
                return {
                    statement: {
                        id: statement.id,
                        title: statement.productStatement
                    },
                    coveredExcluded: regulatedProperty.coveredExcluded,
                    comment: regulatedProperty.comment
                };
            });
            this.syncRegulatedProductStatementsToProduct();
        }
    },
    created() {
        ProductApi.getAttributes().then(r => this.attributes = r.data);
        ProductApi.getAllGeneralProductStatements().then(r => {
            this.generalProductStatements = r.data;
            this.generalProductStatementsLoaded = true;
            this.recalculateProductStatements();
        });
        this.weight = this.productWeight;
    },
    watch: {
        suppliers(suppliers) {
            this.suppliers = suppliers
        },
        product() {
            if (this.product.scipSettings && this.product.scipSettings.primaryKeyType) {
                let type = this.product.scipSettings.primaryKeyType;
                let index = this.product.scipSettings.primaryKeyTypeIndex;
                if (type === ProductIdentifiers.SUPPLIERS_PRODUCT_ID) {
                    for (let i = 0; i < this.product.identifiers.length; ++i) {
                        if (this.showRadioForIdentifier(this.product.identifiers[i])) {
                            type = this.product.identifiers[i].key;
                            index = 0;
                            break;
                        }
                    }

                    if (type === ProductIdentifiers.SUPPLIERS_PRODUCT_ID) {
                        type = ProductIdentifiers.PRODUCT_ID;
                        index = 0;
                    }

                    this.product.scipSettings.primaryKeyType = type;
                    this.product.scipSettings.primaryKeyTypeIndex = index;
                }

                this.productIdentifierEchaRadioValue = this.calculateProductIdentifierRadioValue(
                    type,
                    index
                );
            }

            // this.weight = this.productWeight
            // if (this.product.characteristics) {
            //     this.weight = this.product.characteristics.weight || null;
            // }
            this.recalculateProductStatements();
        },
        productWeight() {
            this.weight = this.productWeight;
        }
    },
    computed: {
        productWeight() {
            if (this.product.characteristics) {
                return this.product.characteristics.weight || null;
            }
            return null;
        },
        showEchaPrimaryKeySelector() {
            if (this.isChemity && this.product.legislations) {
                const scip = Object.values(this.product.legislations).find(l => l.key === LegislationKey.SCIP);
                return scip && scip.isApplicable;
            }
            return false;
        },
        isPurchasedProductComponent() {
            return this.product.type === ProductType.COMPONENT_TO_PURCHASED_PRODUCT
                || this.product.type === ProductType.COMPONENT_TO_COMPLEX_PURCHASED_PRODUCT;
        },
        showIncludedIntoProductGroups() {
            if (this.isGroupsLoaded && this.productGroups.length === 0) {
                return false;
            }
            return !this.isPurchasedProductComponent && this.product.type !== ProductType.PURCHASED_UNDEFINED_SUPPLIER;
        },
        showBusinessRoles() {
            //  HAM-654
            return this.product.type !== ProductType.FICTITIOUS;
        },
        showProductWeight() {
            return !(this.product.type === ProductType.FICTITIOUS || this.product.type === ProductType.GROUP);
        },
        isPurchasedProduct() {
            return this.product.type === ProductType.PURCHASED
                || this.product.type === ProductType.PURCHASED_UNDEFINED_SUPPLIER;
        },
        showComplianceOverview() {
            if (!this.$can('view', 'component:compliance-overview')) {
                return false;
            }
            if (!this.product.type) {
                return false;
            }
            if (this.product.type === ProductType.FICTITIOUS) {
                return false;
            }
            return this.product.type !== ProductType.GROUP;
        },
        showCountryOfOrigin() {
            return false;
            return this.product.type !== ProductType.FICTITIOUS;
        },
        showProductDocument() {
            return this.product.type !== ProductType.FICTITIOUS;
        },
        showProductCategorySelector() {
            return this.product.type !== ProductType.FICTITIOUS;
        },
        showMaterialMixtureCategorySelector() {
            return this.hasMaterialMixtureCategoryLegislation(this.product.legislations, {
                requireApplicable: true,
            });
        },
        showRegulatedProductStatements() {
            const typesToSkip = [
                ProductType.FICTITIOUS,
                ProductType.GROUP,
            ];
            if (typesToSkip.includes(this.product.type)) {
                return false;
            }
            return false !== this.product.isPutOnTheMarket;
        },
        showRohs() {
            if (!this.$can('view', 'component:rohs-exemption')) {
                return false;
            }
            return this.isRohsEnabled && this.product.type !== ProductType.FICTITIOUS;
        },
        showAddButton() {
            return this.addNewComponentButton && (
                this.product.type === ProductType.OWN
                || this.product.type === ProductType.GROUP
                || this.product.type === ProductType.PURCHASED
                || this.product.type === ProductType.PURCHASED_UNDEFINED_SUPPLIER
                || this.product.type === ProductType.COMPONENT_TO_COMPLEX_PURCHASED_PRODUCT
                || this.product.type === ProductType.FICTITIOUS
            );
        },
        showProductOrComponentSearch() {
            return !(
                this.isPurchasedProductComponent
                || this.isPurchasedProduct
            );
        }
    }
}
</script>
